import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICreateCamara,
  IUpdateCamara,
  ICamara,
  TipoDispositivo,
  ICanalesCamara,
  TipoHabilitacion,
  TipoCamara,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { DetallesCanalesCamarasComponent } from './detalles-canales/detalles-canales.component';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { CamarasService } from '../../../../auxiliares/servicios/http/camara.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { LoginService } from '../../../login/login.service';
import { ChequeoIdentificacion } from '../../../../auxiliares/servicios/chequeo-identificacion';

@Component({
  selector: 'app-crear-editar-camara',
  templateUrl: './crear-editar-camara.component.html',
  styleUrl: './crear-editar-camara.component.scss',
  standalone: false,
})
export class CrearEditarCamaraComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<CrearEditarCamaraComponent>>(MatDialogRef, {
    optional: true,
  });
  helper = inject(HelperService);
  private service = inject(CamarasService);
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  private lista2 = inject(Lista2Service);
  paramsService = inject(ParamsService);

  public loading = false;
  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;
  public miCliente = LoginService.getCliente();
  public camara?: ICamara;
  public camara$?: Subscription;
  public tipoCliente = HelperService.getTipoCliente();
  public idCliente?: string;

  public tiposHabilitacion: TipoHabilitacion[] = ['Siempre', 'Con Evento'];

  public tiposDeCamara: TipoCamara[] = [
    'Dahua',
    'Hikvision',
    'Hikvision P2P',
    'Dahua P2P',
  ];

  public tipoModeloSeleccionado: TipoDispositivo | null = null;
  private canalesNVRGuardados: FormGroup[] = [];

  get esNVR() {
    return this.tipoModeloSeleccionado === 'NVR';
  }

  public inputType = 'password';

  get canales() {
    return this.form?.get('canales') as FormArray;
  }

  get mostrarCamposCliente(): boolean {
    return this.tipoCliente !== 'Final';
  }

  get credencialesDahua() {
    return this.form?.get('credencialesDahua') as FormGroup;
  }

  get credencialesHikvision() {
    return this.form?.get('credencialesHikvision') as FormGroup;
  }

  public onClienteChange(cliente: any): void {
    this.idCliente = cliente?._id;
    // El mensaje de identificación repetida aclara el cliente: revalidar.
    this.chequeo.revalidar();
  }

  public chequeo = new ChequeoIdentificacion<ICamara>({
    entidad: 'cámara',
    articulo: 'una',
    campo: 'identificacion',
    control: () => this.form?.get('identificacion'),
    listar: (query) => this.service.getFiltered(query),
    idCliente: () => this.form?.get('idCliente')?.value,
    excluirId: () => this.camara?._id,
  });

  public onClickShowPassword() {
    this.inputType = this.inputType === 'password' ? 'text' : 'password';
  }

  private createForm() {
    const canales: FormGroup[] = [];

    // Determinar el cliente por defecto basado en el tipo de usuario
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.miCliente._id
        : this.camara?.idCliente;

    if (this.camara?.canales) {
      for (const canal of this.camara.canales) {
        canales.push(this.nuevoCanal(canal));
      }
    }

    this.form = new FormGroup({
      identificacion: new FormControl(
        this.camara?.identificacion,
        Validators.required,
        this.chequeo.validador,
      ),
      idModeloDispositivo: new FormControl(this.camara?.idModeloDispositivo),
      idCliente: new FormControl(clienteDefault, Validators.required),
      numeroSerie: new FormControl(this.camara?.numeroSerie),
      host: new FormControl(this.camara?.host),
      puertoHTTP: new FormControl(this.camara?.puertoHTTP),
      puertoRTSP: new FormControl(this.camara?.puertoRTSP),
      usuario: new FormControl(this.camara?.usuario),
      password: new FormControl(this.camara?.password),
      tipo: new FormControl(this.camara?.tipo, Validators.required),
      claveDeEncriptacion: new FormControl(this.camara?.claveDeEncriptacion),
      credencialesDahua: new FormGroup({
        acceskeyDahua: new FormControl(
          this.camara?.credencialesDahua?.acceskeyDahua,
        ),
        secretKeyDahua: new FormControl(
          this.camara?.credencialesDahua?.secretKeyDahua,
        ),
        productID: new FormControl(this.camara?.credencialesDahua?.productID),
      }),
      // Obligatorias para 'Hikvision P2P'
      credencialesHikvision: new FormGroup({
        appKey: new FormControl(this.camara?.credencialesHikvision?.appKey),
        secretKey: new FormControl(
          this.camara?.credencialesHikvision?.secretKey,
        ),
      }),
      canales: new FormArray(canales),
    });

    this.aplicarValidadoresPorTipo(this.camara?.tipo || '', false);
  }

  private actualizarValidadoresNumeroSerie(tipo: string) {
    const numeroSerieControl = this.form?.get('numeroSerie');

    if (!numeroSerieControl) {
      return;
    }

    if (tipo === 'Hikvision P2P') {
      numeroSerieControl.setValidators([
        Validators.required,
        Validators.maxLength(9),
        Validators.pattern(/^[a-zA-Z0-9]+$/),
      ]);
      return;
    }

    if (tipo === 'Dahua P2P') {
      numeroSerieControl.setValidators([
        Validators.required,
        Validators.minLength(15),
      ]);
      return;
    }

    if (tipo === 'Dahua') {
      numeroSerieControl.setValidators([Validators.minLength(15)]);
      return;
    }

    if (tipo === 'Hikvision') {
      numeroSerieControl.setValidators([Validators.minLength(9)]);
      return;
    }

    numeroSerieControl.clearValidators();
  }

  public nuevoCanal(data?: ICanalesCamara) {
    const ids: FormGroup[] = [];
    if (data?.ids) {
      for (const idAct of data.ids) {
        ids.push(
          new FormGroup({
            id: new FormControl(idAct.id),
            width: new FormControl(idAct.width),
            height: new FormControl(idAct.height),
          }),
        );
      }
      return new FormGroup({
        numero: new FormControl(data?.numero, Validators.required),
        nombre: new FormControl(data?.nombre, Validators.required),
        ids: new FormArray(ids, Validators.required),
        descripcion: new FormControl(data?.descripcion),
        idModeloDispositivo: new FormControl(data?.idModeloDispositivo),
        tipoHabilitacion: new FormControl(
          data?.tipoHabilitacion || 'Siempre',
          Validators.required,
        ),
      });
    } else {
      //
      let ultimoCanal = { numero: 0 };
      if (this.form.value.canales && this.form.value.canales.length > 0) {
        ultimoCanal =
          this.form.value.canales[this.form.value.canales.length - 1];
      } else if (this.form.value.tipo === 'Dahua P2P') {
        ultimoCanal = { numero: -1 };
      }
      ids.push(
        new FormGroup({
          id: new FormControl(`${+ultimoCanal.numero + 1}00`),
          width: new FormControl(1920),
          height: new FormControl(1080),
        }),
      );
      ids.push(
        new FormGroup({
          id: new FormControl(`${+ultimoCanal.numero + 1}01`),
          width: new FormControl(1920),
          height: new FormControl(1080),
        }),
      );
      return new FormGroup({
        numero: new FormControl(+ultimoCanal?.numero + 1, Validators.required),
        nombre: new FormControl(
          `Canal ${+ultimoCanal?.numero + 1}`,
          Validators.required,
        ),
        ids: new FormArray(ids, Validators.required),
        descripcion: new FormControl(data?.descripcion),
        idModeloDispositivo: new FormControl(null),
        tipoHabilitacion: new FormControl(
          data?.tipoHabilitacion || 'Siempre',
          Validators.required,
        ),
      });
    }
  }

  public addCanal() {
    if (this.tipoModeloSeleccionado === 'Cámara' && this.canales.length >= 1) {
      return;
    }
    this.canales.push(this.nuevoCanal());
  }

  public onTipoModeloChange(tipo: TipoDispositivo) {
    this.tipoModeloSeleccionado = tipo;
    const modeloCtrl = this.form?.get('idModeloDispositivo');
    modeloCtrl?.setValue(null);
    if (tipo === 'NVR') {
      modeloCtrl?.setValidators(Validators.required);
    } else {
      modeloCtrl?.clearValidators();
    }
    modeloCtrl?.updateValueAndValidity();
    if (tipo === 'Cámara') {
      // Guardar canales extra antes de reducir a 1
      this.canalesNVRGuardados = this.canales.controls.slice(1) as FormGroup[];
      while (this.canales.length > 1) {
        this.canales.removeAt(1);
      }
    }
    if (tipo === 'NVR' && this.canalesNVRGuardados.length > 0) {
      // Restaurar canales guardados
      for (const canal of this.canalesNVRGuardados) {
        this.canales.push(canal);
      }
      this.canalesNVRGuardados = [];
    }
  }

  public async detectarCanales() {
    const res = await this.dialogService.confirm(
      'Detectar Canales',
      `Al detectar, los canales cargados van a ser sobreescritos ¿Continuar?`,
    );
    if (!res) return;
    if (this.camara) {
      const res = await this.service.streamingChannels(this.camara._id);
      if (
        this.camara.tipo === 'Hikvision' &&
        res.StreamingChannelList?.StreamingChannel?.length > 0
      ) {
        const channels = res.StreamingChannelList.StreamingChannel;
        this.canales.clear();
        let idActual;
        const canalData: ICanalesCamara = { numero: '0', ids: [] };
        let canalMultipleData: { id: string; width: number; height: number }[] =
          [];
        for (const channel of channels) {
          idActual = channel.id._text.slice(0, -2);
          if (idActual !== canalData.numero) {
            // Si el canal actual es distinto al anterior
            if (canalData.numero !== '0') {
              //Si no es el primero (creo)
              canalData.ids = canalMultipleData;
              canalData.nombre = `Canal ${canalData.numero}`;
              this.canales.push(this.nuevoCanal(canalData));
            }
            canalMultipleData = [];
            canalData.numero = idActual;
            canalMultipleData.push({
              id: channel.id._text,
              width: channel.Video.videoResolutionWidth._text,
              height: channel.Video.videoResolutionHeight._text,
            });
          } else {
            canalMultipleData.push({
              id: channel.id._text,
              width: channel.Video.videoResolutionWidth._text,
              height: channel.Video.videoResolutionHeight._text,
            });
          }
        }
        if (canalData.numero !== '0') {
          canalData.ids = canalMultipleData;
          canalData.nombre = `Canal ${canalData.numero}`;
          this.canales.push(this.nuevoCanal(canalData));
        }
      }
      if (this.camara.tipo === 'Dahua' && res > 0) {
        this.canales.clear();
        const canalData: ICanalesCamara = { numero: '0', ids: [] };
        for (let i = 1; i <= res; i++) {
          canalData.ids = [
            { id: `${i}01`, width: 1920, height: 1080 },
            { id: `${i}02`, width: 1920, height: 1080 },
          ];
          canalData.numero = `${i}`;
          canalData.nombre = `Canal ${i}`;
          this.canales.push(this.nuevoCanal(canalData));
        }
      }
      if (this.camara.tipo === 'Dahua P2P' && res.channels) {
        this.canales.clear();
        const data = res.channels as string[];
        const canalData: ICanalesCamara = { numero: '0', ids: [] };
        for (const d of data) {
          canalData.ids = [{ id: `${d}`, width: 1920, height: 1080 }];
          canalData.numero = `${d}`;
          canalData.nombre = `Canal ${d}`;
          this.canales.push(this.nuevoCanal(canalData));
        }
      }
    }
  }

  public removeCanal(index: number) {
    this.canales.removeAt(index);
  }

  public async detallesCanal(index: number) {
    this.formularios.abrir(DetallesCanalesCamarasComponent, {
      tamano: 'chico',
      data: this.canales.at(index).value,
      cerrarAfuera: true,
    });
  }

  public ajustarCanal(event: Event, index: number) {
    const numero = (event.target as HTMLInputElement).value;
    let canal: ICanalesCamara;
    if (this.form.value.tipo === 'Dahua') {
      canal = {
        numero: numero,
        nombre: `Canal ${numero}`,
        ids: [
          { id: `${numero}00`, width: 1920, height: 1080 },
          { id: `${numero}01`, width: 1920, height: 1080 },
        ],
      };
    } else {
      canal = {
        numero: numero,
        nombre: `Canal ${numero}`,
        ids: [
          { id: `${numero}00`, width: 1920, height: 1080 },
          { id: `${numero}01`, width: 1920, height: 1080 },
        ],
      };
    }
    this.canales.push(this.nuevoCanal(canal));
    this.removeCanal(index);
  }

  private aplicarValidadoresPorTipo(tipo: string, limpiarCampos: boolean) {
    const numeroSerieControl = this.form.get('numeroSerie');
    const hostControl = this.form.get('host');
    const rtspControl = this.form.get('puertoRTSP');
    const credencialesGroup = this.form.get('credencialesDahua') as FormGroup;
    const acceskeyControl = credencialesGroup?.get('acceskeyDahua');
    const secretKeyControl = credencialesGroup?.get('secretKeyDahua');
    const productIdControl = credencialesGroup?.get('productID');
    const credencialesHikGroup = this.form.get(
      'credencialesHikvision',
    ) as FormGroup;
    const appKeyHikControl = credencialesHikGroup?.get('appKey');
    const secretKeyHikControl = credencialesHikGroup?.get('secretKey');

    if (tipo === 'Dahua' || tipo === 'Hikvision') {
      this.actualizarValidadoresNumeroSerie(tipo);
      hostControl?.setValidators([Validators.required]);
      rtspControl?.setValidators([Validators.required]);
      acceskeyControl?.clearValidators();
      secretKeyControl?.clearValidators();
      productIdControl?.clearValidators();
      appKeyHikControl?.clearValidators();
      secretKeyHikControl?.clearValidators();
      if (limpiarCampos) {
        this.form.patchValue({
          numeroSerie: '',
          claveDeEncriptacion: '',
          credencialesDahua: null,
        });
        // `patchValue(null)` sobre un FormGroup es no-op desde Angular 13
        // (early return), así que para vaciar el grupo hay que resetearlo.
        this.credencialesHikvision?.reset();
      }
    }

    if (tipo === 'Dahua P2P' || tipo === 'Hikvision P2P') {
      this.actualizarValidadoresNumeroSerie(tipo);
      hostControl?.clearValidators();
      rtspControl?.clearValidators();
      if (tipo === 'Dahua P2P') {
        // Opcionales: la cámara transmite igual sin ellas. Solo habilitan
        // consultar el estado del dispositivo y descargar grabaciones.
        acceskeyControl?.clearValidators();
        secretKeyControl?.clearValidators();
        productIdControl?.clearValidators();
        appKeyHikControl?.clearValidators();
        secretKeyHikControl?.clearValidators();
      } else {
        acceskeyControl?.clearValidators();
        secretKeyControl?.clearValidators();
        productIdControl?.clearValidators();
        appKeyHikControl?.setValidators([Validators.required]);
        secretKeyHikControl?.setValidators([Validators.required]);
      }
      if (limpiarCampos) {
        // null y no '': el puerto es numérico y un '' se guardaba tal cual
        // (las apps mobile no podían leer la cámara).
        this.form.patchValue({ host: '', puertoRTSP: null });
      }
    }

    if (
      tipo !== 'Dahua' &&
      tipo !== 'Hikvision' &&
      tipo !== 'Dahua P2P' &&
      tipo !== 'Hikvision P2P'
    ) {
      numeroSerieControl?.clearValidators();
      hostControl?.clearValidators();
      rtspControl?.clearValidators();
      acceskeyControl?.clearValidators();
      secretKeyControl?.clearValidators();
      productIdControl?.clearValidators();
      appKeyHikControl?.clearValidators();
      secretKeyHikControl?.clearValidators();
    }

    numeroSerieControl?.updateValueAndValidity();
    hostControl?.updateValueAndValidity();
    rtspControl?.updateValueAndValidity();
    acceskeyControl?.updateValueAndValidity();
    secretKeyControl?.updateValueAndValidity();
    productIdControl?.updateValueAndValidity();
    appKeyHikControl?.updateValueAndValidity();
    secretKeyHikControl?.updateValueAndValidity();
  }

  public ajustarTipoCamara(tipo: string) {
    this.aplicarValidadoresPorTipo(tipo, true);

    // Limpiar canales al cambiar el tipo de cámara
    this.canales.clear();
  }
  // Listados

  public async onSubmit() {
    this.loading = true;
    try {
      if (!(await this.chequeo.puedeGuardar())) {
        this.helper.notifError(`${this.chequeo.textoRepetida} Elegí otra.`);
        this.loading = false;
        return;
      }

      const data = this.getData();
      if (this.camara?._id) {
        // Update
        const res = await this.service.update(this.camara._id, data);
        this.helper.notifSuccess('Actualizado correctamente');
        if (this.dialogRef) {
          this.dialogRef.close(res);
        } else {
          this.helper.volver();
        }
      } else {
        // Create
        this.camara = await this.service.create(data);
        this.createForm();
        this.setTitles();
        this.helper.notifSuccess('Creado correctamente');
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public getData(): ICreateCamara | IUpdateCamara {
    const data: ICreateCamara | IUpdateCamara = this.form?.getRawValue();

    // Los dos grupos de credenciales existen siempre en el form, así que
    // `getRawValue()` los manda aunque el tipo no los use: por eso una cámara
    // Hikvision P2P terminaba guardada con `credencialesDahua` en null. Se
    // manda solo el grupo del tipo elegido; el otro ni se toca.
    if (data.tipo !== 'Dahua P2P') {
      delete data.credencialesDahua;
    }
    if (data.tipo !== 'Hikvision P2P') {
      delete data.credencialesHikvision;
    }

    // Los puertos son numéricos: vacío o inválido va como null, nunca ''.
    data.puertoRTSP = puertoONull(data.puertoRTSP);
    data.puertoHTTP = puertoONull(data.puertoHTTP);

    return data;
  }

  public volver() {
    if (this.dialogRef) {
      this.dialogRef.close(this.camara?._id);
    } else {
      HelperService.volver();
    }
  }

  private setTitles() {
    if (this.camara) {
      this.title = 'Editar Cámara';
      this.subtitle = this.camara?.identificacion;
    } else {
      this.title = 'Crear Cámara';
    }
  }

  async ngOnInit() {
    this.camara = this.paramsService.getParams()?.['edit'];
    this.createForm();
    this.setTitles();

    // Al editar, determinar tipo según si tiene idModeloDispositivo (NVR) o no (Cámara)
    if (this.camara) {
      if (this.camara.idModeloDispositivo) {
        this.tipoModeloSeleccionado = 'NVR';
        this.form
          .get('idModeloDispositivo')
          ?.setValidators(Validators.required);
        this.form.get('idModeloDispositivo')?.updateValueAndValidity();
      } else {
        this.tipoModeloSeleccionado = 'Cámara';
      }
    }

    // Si es usuario final, establecer automáticamente el cliente
    if (this.tipoCliente === 'Final') {
      this.form.patchValue({
        idCliente: this.miCliente?._id,
      });
      this.idCliente = this.miCliente?._id;
    }

    if (this.dialogRef && this.paramsService.getParams()?.['desdeAlarma']) {
      this.form?.patchValue({
        idCliente: this.paramsService.getParams()?.['idCliente'],
      });
      this.idCliente = this.paramsService.getParams()?.['idCliente'];
    }

    // Inicializar idCliente si estamos editando
    if (this.camara?.idCliente) {
      this.idCliente = this.camara.idCliente;
    }
  }

  ngOnDestroy(): void {
    this.camara$?.unsubscribe();
  }
}

/** Puerto del form → número o null. Los inputs vaciados (o el patch al
 * cambiar a un tipo P2P) dejaban '', que se guardaba como string. */
function puertoONull(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}
